import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import coinbase from '#src/assets/wallet/icon-coinbase.webp';
import metamask from '#src/assets/wallet/icon-metamask.webp';
import opera from '#src/assets/wallet/icon-opera.webp';
import phantom from '#src/assets/wallet/icon-phantom.svg';
import trustWallet from '#src/assets/wallet/icon-trustWallet.svg';
import walletConnect from '#src/assets/wallet/icon-walletConnect.webp';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { ButtonsList } from '#src/atoms/buttons/ButtonsList/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { ORIGIN } from '#src/config.ts';
import { useBlockchainContext } from '#src/contexts/blockchain/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { useEphemeralLink } from '#src/hooks/users/useEphemeralLink.ts';

const StyledImg = styled.img`
  max-width: var(--quadruple-unit);
  max-height: var(--quadruple-unit);
`;
const ButtonText = styled.div`
  text-align: center;
  width: 100%;
`;

const externalLinkProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const;

function stripScheme(url: string) {
  const { host, pathname, search, hash } = new URL(url, ORIGIN);
  return `${host}${pathname}${search}${hash}`;
}

export const RecommendedWalletApp = () => {
  const { up: isLaptop } = useScreenSize('laptop');
  const { setupWalletConnect, setupCoinbaseWallet, setupOpera } =
    useBlockchainContext();
  const [showMoreOptions, toggleShowMoreOptions] = useToggle(false);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const createEphemeralLink = useEphemeralLink();

  useEffect(() => {
    if (!redirectUrl) {
      const url = `${ORIGIN}?action=addFundsEth`;
      createEphemeralLink(url).then(link => {
        if (link) {
          setRedirectUrl(link);
        } else {
          setRedirectUrl(url);
        }
      });
    }
  }, [redirectUrl, createEphemeralLink]);

  if (!redirectUrl) return <LoadingIndicator />;

  const METAMASK_MOBILE_APP_LINK = `https://metamask.app.link/dapp/${stripScheme(redirectUrl)}`;
  const COINBASE_MOBILE_APP_LINK = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(redirectUrl)}`;
  const TRUST_WALLET_MOBILE_APP_LINK = `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(redirectUrl)}`;

  const mobileButtons = (
    <>
      <a href={METAMASK_MOBILE_APP_LINK} {...externalLinkProps}>
        <StyledImg alt="Metamask" src={metamask} />
        <BodyM>Metamask</BodyM>
      </a>
      {window.ethereum?.isCoinbaseWallet ? (
        <ButtonBase onClick={setupCoinbaseWallet}>
          <StyledImg alt="coinbase" src={coinbase} />
          <BodyM>Coinbase Wallet</BodyM>
        </ButtonBase>
      ) : (
        <a href={COINBASE_MOBILE_APP_LINK} {...externalLinkProps}>
          <StyledImg alt="coinbase" src={coinbase} />
          <BodyM>Coinbase Wallet</BodyM>
        </a>
      )}
      <a href={TRUST_WALLET_MOBILE_APP_LINK} {...externalLinkProps}>
        <StyledImg alt="Trust Wallet" src={trustWallet} />
        <BodyM>Trust Wallet</BodyM>
      </a>
      {!!showMoreOptions && (
        <>
          <ButtonBase onClick={setupWalletConnect}>
            <StyledImg alt="Wallet Connect" src={walletConnect} />
            <BodyM>Wallet Connect</BodyM>
          </ButtonBase>
          <ButtonBase onClick={setupOpera}>
            <StyledImg alt="opera" src={opera} />
            <BodyM>Opera</BodyM>
          </ButtonBase>
        </>
      )}
    </>
  );

  const desktopButtons = (
    <>
      <a href="https://metamask.io/" {...externalLinkProps}>
        <StyledImg alt="Metamask" src={metamask} />
        <BodyM>Metamask</BodyM>
      </a>
      <ButtonBase onClick={setupCoinbaseWallet}>
        <StyledImg alt="Coinbase" src={coinbase} />
        <BodyM>Coinbase Wallet</BodyM>
      </ButtonBase>
      <a
        href="https://trustwallet.com/browser-extension"
        {...externalLinkProps}
      >
        <StyledImg alt="Trust Wallet" src={trustWallet} />
        <BodyM>Trust Wallet</BodyM>
      </a>
      <a href="https://phantom.app/download" {...externalLinkProps}>
        <StyledImg alt="Phantom" src={phantom} />
        <BodyM>Phantom</BodyM>
      </a>
      {!!showMoreOptions && (
        <>
          <ButtonBase onClick={setupWalletConnect}>
            <StyledImg alt="Wallet Connect" src={walletConnect} />
            <BodyM>Wallet Connect</BodyM>
          </ButtonBase>
          <ButtonBase onClick={setupOpera}>
            <StyledImg alt="opera" src={opera} />
            <BodyM>Opera</BodyM>
          </ButtonBase>
        </>
      )}
    </>
  );

  return (
    <ButtonsList>
      {isLaptop ? desktopButtons : mobileButtons}
      <ButtonBase onClick={toggleShowMoreOptions}>
        <ButtonText>
          <BodyM>
            {showMoreOptions ? (
              <FormattedMessage defaultMessage="Show less" />
            ) : (
              <FormattedMessage defaultMessage="Show more" />
            )}
          </BodyM>
        </ButtonText>
      </ButtonBase>
    </ButtonsList>
  );
};
